<template>
  <el-config-provider :locale="elLocale">
    <el-container class="app-layout">
      <el-aside
        :width="isMenuCollapsed ? '60px' : '260px'"
        class="sidebar-container"
      >
        <div class="sidebar-header">
          <el-tooltip v-if="isMenuCollapsed" :content="$t('menu.expandSidebar')" placement="right">
            <div class="collapsed-logo" @click="toggleMenuCollapse">
              <img :src="logoNodb" height="36" draggable="false">
              <div class="expand-overlay">
                <img :src="menuExpand" :alt="$t('menu.expandSidebar')" width="16" height="16">
              </div>
            </div>
          </el-tooltip>
          <template v-else>
            <img :src="logoNodb" height="36" draggable="false">
            <span class="sidebar-title">НОБД</span>
            <el-tooltip :content="$t('menu.collapseSidebar')" placement="left">
              <el-button class="collapse-button" size="default" @click="toggleMenuCollapse">
                <img :src="menuCollapse" :alt="$t('menu.collapseSidebar')" width="16" height="16">
              </el-button>
            </el-tooltip>
          </template>
        </div>

        <el-scrollbar aria-orientation="vertical" class="sidebar-scroll">
          <v-general-menu
            v-if="authStore.isReady"
            :collapse="isMenuCollapsed"
          />
        </el-scrollbar>

        <div class="sidebar-footer">
          <el-row class="mb-14" :justify="isMenuCollapsed ? 'center' : 'start'">
            <locale-change :collapse="isMenuCollapsed" />
          </el-row>
          <template v-if="authStore.isReady">
            <div v-if="!isMenuCollapsed" class="bottom-section">
              <scope-selector :collapse="isMenuCollapsed" class="flex-grow" />
              <el-tooltip
                :content="$t('action.logout')"
                placement="right"
              >
                <el-button
                  type="primary"
                  :loading="loading"
                  style="width: 32px; height: 32px; border-radius: 6px; flex-shrink: 0;"
                  @click="handleLogout"
                >
                  <el-icon><Back /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
            <el-row v-else justify="center" style="margin-top: 1rem;">
              <el-tooltip
                :content="$t('action.logout')"
                placement="right"
              >
                <el-button
                  type="primary"
                  :loading="loading"
                  style="width: 32px; height: 32px; border-radius: 6px;"
                  @click="handleLogout"
                >
                  <el-icon><Back /></el-icon>
                </el-button>
              </el-tooltip>
            </el-row>
          </template>
        </div>
      </el-aside>

      <el-main class="main-content">
        <router-view :key="`${$route.params.entity}`" />
      </el-main>
    </el-container>
  </el-config-provider>
</template>

<script setup lang="ts">
import localeRu from 'element-plus/es/locale/lang/ru.mjs'
import localeKk from 'element-plus/es/locale/lang/kk.mjs'
import VGeneralMenu from '@/components/VAsideMenu.vue'
import LocaleChange from '@/components/LocaleChange.vue'
import ScopeSelector from '@/components/ScopeSelector.vue'
import {Locale, useLocale} from '@/composables/locale'
import {useAuthStore} from '@/stores/auth'
import {computed, ref} from 'vue'
import {Back} from '@element-plus/icons-vue'
import logoNodb from '@/assets/icons/nodb-logo.svg'
import menuCollapse from '@/assets/icons/menu-collapse.svg'
import menuExpand from '@/assets/icons/menu-expand.svg'

const {locale} = useLocale()
const authStore = useAuthStore()

const loading = ref(false)
const isMenuCollapsed = ref(false)

localeKk.el.pageHeader.title = 'Артқа'

const elLocale = computed(() => locale.value === Locale.Ru ? localeRu : localeKk)

async function handleLogout() {
  loading.value = true
  await authStore.logout()
  loading.value = false
}

function toggleMenuCollapse() {
  isMenuCollapsed.value = !isMenuCollapsed.value
}
</script>

<style scoped>
.app-layout {
  height: 100vh;
  overflow: hidden;
}

.sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-right: 1px solid var(--el-border-color);
  transition: width 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-header {
  flex-shrink: 0;
  padding: 0 16px;
  height: 56px;
  display: flex;
  align-items: center;
  column-gap: 8px;
  justify-content: space-between;
  overflow: hidden;
  font-size: 22px;
  color: var(--sidebar-text-color);
  user-select: none;
  font-family: var(--landing-font-family);
}

.sidebar-header > img {
  html.dark & {
    filter: brightness(0) invert(1) brightness(0.75);
  }
}

.sidebar-title {
  white-space: nowrap;
}

.collapsed-logo {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.collapsed-logo > img {
  transition: opacity 0.2s;

  html.dark & {
    filter: brightness(0) invert(1) brightness(0.75);
  }
}

.expand-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  background: var(--sidebar-overlay-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 4px;
}

.expand-overlay img {
  filter: var(--sidebar-icon-filter);
}

.collapsed-logo:hover > img {
  opacity: 0;
}

.collapsed-logo:hover .expand-overlay {
  opacity: 1;
}

.collapse-button {
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--sidebar-text-color);
  width: 32px;
  height: 32px;
  padding: 0;
}

.collapse-button:hover {
  background: var(--sidebar-overlay-bg);
}

.collapse-button img {
  filter: var(--sidebar-icon-filter);
}

.sidebar-scroll {
  flex: 1;
  min-height: 0;
}

.sidebar-footer {
  flex-shrink: 0;
  padding: 1rem;
  border-top: 1px solid var(--el-border-color);
}

.main-content {
  overflow-y: auto;
  height: 100%;
}

.bottom-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--el-border-color);
  margin-top: 0.7rem;
}

.flex-grow {
  flex: 1;
  min-width: 0;
}
</style>
